import { createContext, type ReactNode, useContext, useSyncExternalStore } from 'react';

import { subscribeAppliedTheme } from './appliedTheme';
import { getDocumentChartTheme } from './readChartTheme';
import type { ChartTheme } from './types';

const ChartThemeContext = createContext<ChartTheme | null>(null);

interface ChartThemeOverrideProps {
    theme: ChartTheme;
    children: ReactNode;
}

/**
 * Gives a subtree a fixed chart theme instead of the page's, for example to render a chart
 * in light for a printed report whatever the user has picked. Build the theme with
 * `readChartTheme`.
 */
export function ChartThemeOverride({ theme, children }: ChartThemeOverrideProps) {
    return <ChartThemeContext value={theme}>{children}</ChartThemeContext>;
}

/**
 * The chart colours of the applied theme, re-read and re-rendered when the theme switches.
 *
 * The returned object is shared by every chart and only changes with the theme, so it can
 * go straight into a `useMemo` dependency list. Needs no provider: outside ThemeProvider
 * (tests, isolated renders) it reads the light tokens.
 */
export function useChartTheme(): ChartTheme {
    const override = useContext(ChartThemeContext);
    const documentTheme = useSyncExternalStore(
        subscribeAppliedTheme,
        getDocumentChartTheme,
        getDocumentChartTheme
    );
    return override ?? documentTheme;
}
